import { useEffect, useState, useCallback } from "react";
import { EmailSuggestInput } from "../../components/EmailSuggestInput";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Link2, Copy, IndianRupee, ClipboardCheck, Truck, CheckCircle2, RefreshCw, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const HEALTH_STATE = (h) => {
  if (!h || h.configured === false) return "off";
  if (h.reachable === true) return h.auth_failed ? "warn" : "ok";
  return "down";
};
const HEALTH_META = {
  ok:   { cls: "text-emerald-300 border-emerald-500/50", dot: "bg-emerald-400", label: "Reachable" },
  warn: { cls: "text-amber-300 border-amber-500/50",     dot: "bg-amber-400",   label: "Keys rejected" },
  down: { cls: "text-red-400 border-red-500/50",         dot: "bg-red-400",     label: "Unreachable" },
  off:  { cls: "text-slate-500 border-white/15",         dot: "bg-slate-500",   label: "Not configured" },
};

const GatewayHealthBadge = ({ h, pinging, gw }) => {
  if (pinging && !h) {
    return <span className="text-[10px] tracking-[0.14em] uppercase border border-white/15 text-slate-400 px-2.5 py-1 inline-flex items-center gap-1.5" data-testid={`gateway-health-${gw}`}><RefreshCw size={11} className="animate-spin" /> Pinging…</span>;
  }
  if (!h) return null;
  const s = HEALTH_STATE(h);
  const m = HEALTH_META[s];
  return (
    <span title={h.detail || m.label} className={`text-[10px] tracking-[0.14em] uppercase border px-2.5 py-1 inline-flex items-center gap-1.5 ${m.cls}`} data-testid={`gateway-health-${gw}`} data-health={s}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot} ${s === "ok" ? "animate-pulse" : ""}`} />
      {m.label}
    </span>
  );
};

const fmtError = (d) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d?.msg || "Something went wrong.";
};

const fmtDate = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const KIND_LABEL = { assessment: "Assessment", invoice: "Invoice", product: "Product", service: "Service", order: "Store Order" };

export const AdminPayments = ({ onAuthFail, readOnly = false, isAdmin = false }) => {
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_email: "", amount_inr: "", reference: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [payu, setPayu] = useState({ configured: false, mid: "" });
  const [keys, setKeys] = useState({ merchant_key: "", salt: "" });
  const [savingKeys, setSavingKeys] = useState(false);
  const [rzp, setRzp] = useState({ configured: false, mode: "test" });
  const [rzpKeys, setRzpKeys] = useState({ key_id: "", key_secret: "", mode: "test" });
  const [savingRzp, setSavingRzp] = useState(false);
  const [health, setHealth] = useState(null);
  const [pinging, setPinging] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadHealth = useCallback(async () => {
    if (!isAdmin) return; // health check is admin-only
    setPinging(true);
    try {
      const { data } = await api.get("/admin/gateway-health");
      setHealth(data);
    } catch { /* ignore — badge simply stays hidden */ } finally {
      setPinging(false);
    }
  }, [isAdmin]);

  const loadRzp = useCallback(async () => {
    if (!isAdmin) return; // gateway credentials are admin-only
    try {
      const { data } = await api.get("/admin/razorpay-settings");
      setRzp(data);
    } catch { /* ignore */ }
  }, []);

  const setRzpMode = async (mode) => {
    if (rzp.configured) {
      try {
        const { data } = await api.post("/admin/razorpay-settings", { key_id: "", key_secret: "", mode });
        setRzp(data);
        toast.success(mode === "test" ? "Razorpay sandbox mode — public checkout hides Razorpay; use /payment-test for card tests" : "Razorpay live mode — public checkout shows Razorpay");
      } catch { toast.error("Could not change mode"); }
    }
    setRzpKeys((k) => ({ ...k, mode }));
  };

  const saveRzp = async (e) => {
    e.preventDefault();
    setSavingRzp(true);
    try {
      const { data } = await api.post("/admin/razorpay-settings", rzpKeys);
      setRzp(data);
      setRzpKeys({ key_id: "", key_secret: "", mode: rzpKeys.mode });
      toast.success("Razorpay credentials saved");
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error(fmtError(err?.response?.data?.detail));
    } finally {
      setSavingRzp(false);
    }
  };

  const loadPayu = useCallback(async () => {
    if (!isAdmin) return; // gateway credentials are admin-only
    try {
      const { data } = await api.get("/admin/payu-settings");
      setPayu(data);
    } catch { /* ignore */ }
  }, []);

  const saveKeys = async (e) => {
    e.preventDefault();
    setSavingKeys(true);
    try {
      const { data } = await api.post("/admin/payu-settings", keys);
      setPayu(data);
      setKeys({ merchant_key: "", salt: "" });
      toast.success("PayU credentials saved — live payments are now active");
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error(fmtError(err?.response?.data?.detail));
    } finally {
      setSavingKeys(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/payments");
      setPayments(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error("Could not load payments");
    }
  }, [onAuthFail]);

  useEffect(() => { load(); loadPayu(); loadRzp(); loadHealth(); }, [load, loadPayu, loadRzp, loadHealth]);

  const createLink = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/invoice-link", { ...form, amount_inr: parseInt(form.amount_inr, 10) });
      toast.success(`Payment link emailed to ${form.client_email}`);
      try { await navigator.clipboard.writeText(data.link_url); toast.info("Link copied to clipboard"); } catch { /* clipboard optional */ }
      setForm({ client_name: "", client_email: "", amount_inr: "", reference: "", description: "" });
      load();
    } catch (err) {
      toast.error(fmtError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Could not copy"); }
  };

  const advance = async (p, status) => {
    try {
      await api.patch(`/payments/${p.id}/fulfilment`, { status });
      toast.success(`Marked as ${status} — customer emailed`);
      load();
    } catch (err) {
      toast.error(fmtError(err?.response?.data?.detail));
    }
  };

  const fulfilBtn = "text-[11px] tracking-[0.1em] uppercase border px-2.5 py-1.5 transition-colors";
  const FulfilmentFlow = ({ p }) => {
    const f = p.fulfilment || "";
    if (readOnly || !isAdmin) return null;
    return (
      <span className="flex flex-wrap gap-2" data-testid={`fulfilment-${p.id}`}>
        {f === "" && <button onClick={() => advance(p, "confirmed")} className={`${fulfilBtn} text-white border-white/20 hover:border-nx-gold/60`} data-testid={`fulfil-confirm-${p.id}`}><ClipboardCheck size={12} className="inline mr-1" />Confirm</button>}
        {(f === "" || f === "confirmed") && <button onClick={() => advance(p, "dispatched")} className={`${fulfilBtn} text-sky-300 border-sky-500/40 hover:border-sky-400`} data-testid={`fulfil-dispatch-${p.id}`}><Truck size={12} className="inline mr-1" />Dispatch</button>}
        {f && f !== "delivered" && <button onClick={() => advance(p, "delivered")} className={`${fulfilBtn} text-emerald-300 border-emerald-500/40 hover:border-emerald-400`} data-testid={`fulfil-deliver-${p.id}`}><CheckCircle2 size={12} className="inline mr-1" />Deliver</button>}
        {f === "delivered" && <span className="text-[11px] tracking-[0.1em] uppercase text-emerald-300/70 px-2.5 py-1.5">Delivered ✓</span>}
      </span>
    );
  };

  const totalReceived = payments.filter((p) => p.status === "paid" && p.gateway !== "razorpay-test").reduce((s, p) => s + (p.amount_inr || 0), 0);
  const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-2.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

  return (
    <div className="flex flex-col gap-8" data-testid="admin-payments">
      <div className="border border-nx-gold/40 bg-white/[0.02] p-5 sm:p-6" data-testid="payu-settings" style={!isAdmin ? { display: "none" } : undefined}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><CreditCard size={13} className="text-nx-goldlight" /> PayU Gateway Settings (MID {payu.mid || "13732259"} · production)</p>
          <span className={`text-[10px] tracking-[0.14em] uppercase border px-2.5 py-1 ${payu.configured ? "text-emerald-300 border-emerald-500/50" : "text-amber-300 border-amber-500/40"}`} data-testid="payu-configured-chip">
            {payu.configured ? "Live payments active" : "Awaiting credentials"}
          </span>
          <GatewayHealthBadge h={health?.payu} pinging={pinging} gw="payu" />
          <button type="button" onClick={loadHealth} disabled={pinging} className="ml-auto text-[10px] tracking-[0.14em] uppercase text-slate-400 hover:text-nx-goldlight border border-white/15 hover:border-nx-gold/50 px-2.5 py-1 inline-flex items-center gap-1.5 transition-colors disabled:opacity-50" data-testid="gateway-health-recheck">
            <Activity size={11} className={pinging ? "animate-pulse" : ""} /> Re-check
          </button>
        </div>
        <form onSubmit={saveKeys} className="flex flex-col gap-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input required type="password" placeholder="PayU Merchant Key" className={input} value={keys.merchant_key} onChange={(e) => setKeys((k) => ({ ...k, merchant_key: e.target.value }))} data-testid="payu-merchant-key" autoComplete="off" />
            <input required type="password" placeholder="PayU Salt" className={input} value={keys.salt} onChange={(e) => setKeys((k) => ({ ...k, salt: e.target.value }))} data-testid="payu-salt" autoComplete="off" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button type="submit" disabled={savingKeys} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3 transition-colors disabled:opacity-60" data-testid="payu-save-keys">
              {savingKeys ? "Saving…" : payu.configured ? "Update Credentials" : "Save & Activate Live Payments"}
            </button>
            <p className="text-[11px] text-slate-500">Stored encrypted on the server, never shown again, never sent to the browser. Paste from your PayU dashboard (MID 13732259).</p>
          </div>
        </form>
      </div>

      <div className="border border-white/15 bg-white/[0.02] p-5 sm:p-6" data-testid="razorpay-settings" style={!isAdmin ? { display: "none" } : undefined}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><CreditCard size={13} className="text-nx-goldlight" /> Razorpay Gateway Settings</p>
          <span className={`text-[10px] tracking-[0.14em] uppercase border px-2.5 py-1 ${rzp.configured ? (rzp.mode === "test" ? "text-amber-300 border-amber-500/40" : "text-emerald-300 border-emerald-500/50") : "text-amber-300 border-amber-500/40"}`} data-testid="razorpay-configured-chip">
            {rzp.configured ? (rzp.mode === "test" ? "Sandbox mode" : "Razorpay live") : "Awaiting credentials"}
          </span>
          <GatewayHealthBadge h={health?.razorpay} pinging={pinging} gw="razorpay" />
        </div>
        <form onSubmit={saveRzp} className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10px] tracking-[0.14em] uppercase text-slate-500 whitespace-nowrap">Mode:</span>
            <button type="button" onClick={() => setRzpMode("live")} className={`text-[10px] tracking-[0.12em] uppercase px-3 py-1.5 border transition-colors ${(rzp.configured ? rzp.mode : rzpKeys.mode) === "live" ? "border-emerald-500/60 text-emerald-300" : "border-white/15 text-slate-500"}`} data-testid="razorpay-mode-live">Live / Production</button>
            <button type="button" onClick={() => setRzpMode("test")} className={`text-[10px] tracking-[0.12em] uppercase px-3 py-1.5 border transition-colors ${(rzp.configured ? rzp.mode : rzpKeys.mode) === "test" ? "border-amber-500/60 text-amber-300" : "border-white/15 text-slate-500"}`} data-testid="razorpay-mode-test">Sandbox (card testing)</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input required type="password" placeholder="Razorpay Key ID (rzp_live_… / rzp_test_…)" className={input} value={rzpKeys.key_id} onChange={(e) => setRzpKeys((k) => ({ ...k, key_id: e.target.value }))} data-testid="razorpay-key-id" autoComplete="off" />
            <input required type="password" placeholder="Razorpay Key Secret" className={input} value={rzpKeys.key_secret} onChange={(e) => setRzpKeys((k) => ({ ...k, key_secret: e.target.value }))} data-testid="razorpay-key-secret" autoComplete="off" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button type="submit" disabled={savingRzp} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3 transition-colors disabled:opacity-60" data-testid="razorpay-save-keys">
              {savingRzp ? "Saving…" : rzp.configured ? "Update Credentials" : "Save & Activate Razorpay"}
            </button>
            <p className="text-[11px] text-slate-500">From your Razorpay dashboard → Settings → API Keys. Stored encrypted on the server, never shown again.</p>
          </div>
          {(rzp.configured ? rzp.mode : rzpKeys.mode) === "test" && (
            <p className="text-[11px] text-amber-300/80">Sandbox mode: Razorpay is hidden from public checkout. Run card tests at <span className="text-amber-300">nexsecure.in/payment-test</span> — test payments are tagged and never counted in revenue.</p>
          )}
        </form>
      </div>

      <div className="border border-white/10 bg-white/[0.02] p-5 flex items-center gap-4" data-testid="payments-total">
        <span className="text-nx-goldlight"><IndianRupee size={20} /></span>
        <div>
          <p className="text-2xl font-semibold text-white leading-none">₹{totalReceived.toLocaleString("en-IN")}</p>
          <p className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mt-1.5">Received online ({payments.filter((p) => p.status === "paid").length} payments)</p>
        </div>
      </div>

      {readOnly ? (
        <p className="text-xs text-slate-500 border border-white/10 px-4 py-3" data-testid="payments-readonly-note">View-only access — you can see received payments. Creating links and gateway settings are restricted to the Accounts role and administrator.</p>
      ) : (
      <form onSubmit={createLink} className="border border-white/10 bg-white/[0.02] p-5 sm:p-6 flex flex-col gap-4" data-testid="invoice-link-form">
        <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><Link2 size={13} className="text-nx-goldlight" /> Email a client a payment link (invoice / AMC / any amount)</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input required placeholder="Client name" className={input} value={form.client_name} onChange={set("client_name")} data-testid="invoice-client-name" />
          <EmailSuggestInput required type="email" placeholder="Client email" className={input} value={form.client_email} onChange={set("client_email")} data-testid="invoice-client-email" />
          <input required type="number" min="1" max="1000000" placeholder="Amount in ₹ (e.g. 15000)" className={input} value={form.amount_inr} onChange={set("amount_inr")} data-testid="invoice-amount" />
          <input required placeholder="Invoice no. / reference" className={input} value={form.reference} onChange={set("reference")} data-testid="invoice-reference" />
        </div>
        <input placeholder="What is this payment for? (optional note to client)" className={input} value={form.description} onChange={set("description")} data-testid="invoice-description" />
        <div>
          <button type="submit" disabled={busy} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3 transition-colors disabled:opacity-60" data-testid="invoice-create-button">
            {busy ? "Creating…" : "Create Link & Email Client"}
          </button>
        </div>
      </form>
      )}

      <div className="flex flex-col gap-3">
        {payments.length === 0 && <p className="text-slate-500 text-sm" data-testid="payments-empty">No payments yet — invoice links you create and priority bookings will appear here.</p>}
        {payments.map((p) => (
          <div key={p.id} className="border border-white/10 bg-white/[0.02] px-5 py-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid={`payment-row-${p.id}`}>
            <span className={`text-[10px] tracking-[0.14em] uppercase border px-2.5 py-1 ${p.status === "paid" ? "text-emerald-300 border-emerald-500/50" : p.status === "failed" ? "text-red-400 border-red-500/50" : "text-amber-300 border-amber-500/40"}`} data-testid={`payment-status-${p.id}`}>{p.status === "created" ? "awaiting" : p.status}</span>
            <span className="text-[10px] tracking-[0.14em] uppercase text-nx-goldlight border border-nx-gold/40 px-2.5 py-1">{KIND_LABEL[p.kind] || p.kind}</span>
            {p.gateway === "razorpay-test" && <span className="text-[10px] tracking-[0.14em] uppercase border border-amber-500/50 text-amber-300 px-2 py-1" data-testid="payment-test-chip">Test</span>}
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{p.name} · <span className="text-slate-400">{p.reference}</span></p>
              <p className="text-xs text-slate-500 truncate">{p.email} · {fmtDate(p.created_at)}</p>
            </div>
            <span className="text-white font-semibold ml-auto">₹{(p.amount_inr || 0).toLocaleString("en-IN")}</span>
            {p.status === "paid" && p.kind !== "invoice" && <FulfilmentFlow p={p} />}
            {p.link_url && p.status !== "paid" && (
              <button onClick={() => copyLink(p.link_url)} className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-nx-goldlight border border-white/15 hover:border-nx-gold/50 px-2.5 py-1.5 transition-colors" data-testid={`payment-copy-${p.id}`}>
                <Copy size={12} /> Link
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-600 flex items-center gap-2"><CreditCard size={12} /> Payments are processed by Razorpay and PayU — NexSecure never sees or stores card details.</p>
    </div>
  );
};
